import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { RootState, AppDispatch } from '../store/store';
import { updateObjective } from '../store/map3PendingSlice';
import { selectUserProfile } from '../store/selectors';
import { cloneDeep } from '../utils/cloneDeep';
import { useRoleWithOverride } from '../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '../utils/getPermissionsForRole';
import { getIntervalsForMilestoneMap } from '../utils/getIntervalsForMilestoneMap';
import { createResult1 } from '@mm-shared/types/Result1.utils';
import type { Result1 } from '@mm-shared/types/Result1';
import { Map3ImplementationPlans } from './Map3ImplementationPlans';
import { deriveCards, boardLayoutKey } from './utils/planLayout';
import { cardStatusIntervals } from './utils/cardStatus';
import type { MoveCardHandler } from './hooks/usePlanNodesChangeHandler';

/**
 * Map3ImplementationPlansWrapper — Redux boundary for the Implementation Plans
 * view (ADR §2, §3).
 *
 * Follows the Map3RelativesWrapper pattern: selects and deep-copies Redux data,
 * derives plain PlanCards, and passes them (plus the ADR §2 layout key) to the
 * presentational canvas, which never imports Redux. Every write handler lives
 * here — move, title edit, add key result, and the key-result text edit
 * (ADR A3) — and every one of them reads the objective fresh from the store at
 * commit time rather than from a render-time closure.
 *
 * Invariant: this view reads milestoneMap.objectiveUuids for membership and
 * implicit-rank order, and NEVER writes it (ADR §1 — reordering that array
 * reparents objectives in Map3).
 */
export function Map3ImplementationPlansWrapper() {
  const { mapShortId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();

  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.shortId === mapShortId),
  );
  const objectives = useSelector((state: RootState) => state.map3Data.objectives);

  const userRole = useRoleWithOverride(milestoneMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  const mapUuid = milestoneMap?.uuid;

  /**
   * Persists one card move (ADR §1e). The implementationPlan map is read FRESH
   * from the store at commit time, not from a render-time closure — the
   * Map3.tsx Bug 7382485 remedy — so a concurrent placement on another map is
   * not reverted by this write. P1: column and rank are always defined strings;
   * no undefined ever enters the payload (objective_update lacks
   * stripUndefinedFields and would throw at write time).
   */
  const handleMoveCard = useCallback<MoveCardHandler>(
    (objectiveUuid, column, rank) => {
      if (!mapUuid || !canEdit) return;
      const fresh = store
        .getState()
        .map3Data.objectives.find((o) => o.uuid === objectiveUuid);
      dispatch(
        updateObjective({
          uuid: objectiveUuid,
          updates: {
            implementationPlan: {
              ...(fresh?.implementationPlan ?? {}),
              [mapUuid]: { column, rank },
            },
          },
        }),
      );
    },
    [dispatch, store, mapUuid, canEdit],
  );

  /**
   * Persists a title edit (ADR §5). Only {text} is written — never a partial
   * results array or any other field.
   */
  const handleEditTitle = useCallback(
    (objectiveUuid: string, text: string) => {
      const trimmed = text.trim();
      if (!canEdit || !trimmed) return;
      dispatch(updateObjective({ uuid: objectiveUuid, updates: { text: trimmed } }));
    },
    [dispatch, canEdit],
  );

  /**
   * Persists a new key result (ADR §5): createResult1 with exactly the four
   * explicit props the ADR names — text, isKeyResult, deadline (the map's last
   * interval end, falling back to endDate), createdBy — everything else on
   * factory defaults. The results array is read FRESH from the store and
   * replaced wholesale (handleChange does not merge arrays; a partial write
   * silently truncates — ADR §3).
   */
  const handleAddKeyResult = useCallback(
    (objectiveUuid: string, text: string) => {
      const trimmed = text.trim();
      if (!canEdit || !trimmed || !milestoneMap) return;
      const state = store.getState();
      const fresh = state.map3Data.objectives.find((o) => o.uuid === objectiveUuid);
      if (!fresh) return;

      const lastIntervalEnd =
        getIntervalsForMilestoneMap(milestoneMap).at(-1)?.end.toISOString() ??
        milestoneMap.endDate;
      const newResult = createResult1({
        text: trimmed,
        isKeyResult: true,
        deadline: lastIntervalEnd,
        createdBy: selectUserProfile(state)?.userName ?? '',
      });
      dispatch(
        updateObjective({
          uuid: objectiveUuid,
          updates: { results: [...(fresh.results ?? []), newResult] },
        }),
      );
    },
    [dispatch, store, canEdit, milestoneMap],
  );

  /**
   * Persists a key-result **text** edit (ADR A3c). Four properties, each
   * load-bearing:
   *
   * 1. **Store-fresh, whole-array replacement.** `handleChange`'s
   *    `objective_update` branch is a whole-array fallback, so a partial
   *    `results` write silently truncates the objective (ADR §3, §1e's Bug
   *    7382485 remedy). The array is read at commit time, never from a
   *    render-time closure.
   * 2. **Non-key results are carried through untouched**, by reference. The
   *    card renders only `isKeyResult` rows, so a card edit must never drop,
   *    reorder or rewrite a row it does not show.
   * 3. **Only the edited row passes through `createResult1`** — the app's
   *    established single-row edit shape (`Map3Drawer.tsx:422`,
   *    `ResultItem2.tsx:219`, `ResultItem3.tsx:273`). The factory is an
   *    allowlist reconstructor, not a normalizer: it rebuilds a new literal
   *    from a fixed field list, forces `forIntervalEnding` to `deadline`, and
   *    silently **drops `Result1.owner`** (a pre-existing shared-factory gap
   *    shared by every current caller — ADR A3c property 5, tracked as bead
   *    mr-b380). Confining the factory to the one edited row bounds that blast
   *    radius to the row the user typed in.
   * 4. **P1 (no `undefined` in the payload) holds** via the factory's throw on
   *    a falsy `uuid`/`shortId`/`text`/`deadline`/`createdDate`, which is
   *    caught here and converted into "no dispatch" — the same treatment §1e
   *    gives a `rankBetween` throw. A legacy row with neither `deadline` nor
   *    `forIntervalEnding` therefore reverts to its stored text with no write
   *    (ADR A3c-i: a deliberate, tested silent-failure path).
   *
   * `isKeyResult` is **preserved, never set** (A3d): the handler guards on it
   * and dispatches nothing when the target is not a key result. The only way
   * to reach that guard is a remote demotion between render and submit, and
   * forcing `true` would silently revert that collaborator's change as a side
   * effect of a typo fix.
   */
  const handleEditKeyResult = useCallback(
    (objectiveUuid: string, resultUuid: string, text: string) => {
      if (!canEdit) return;
      const fresh = store
        .getState()
        .map3Data.objectives.find((o) => o.uuid === objectiveUuid);
      const target = fresh?.results?.find((r) => r.uuid === resultUuid);
      const trimmed = text.trim();
      if (!fresh || !target || !target.isKeyResult) return;
      if (!trimmed || trimmed === target.text) return;
      let edited: Result1;
      try {
        edited = createResult1({ ...target, text: trimmed });
      } catch {
        return;
      }
      dispatch(
        updateObjective({
          uuid: objectiveUuid,
          updates: {
            results: (fresh.results ?? []).map((r) => (r.uuid === resultUuid ? edited : r)),
          },
        }),
      );
    },
    [dispatch, store, canEdit],
  );

  /**
   * Persists a key-result **delivered** toggle (ADR.ImplementationPlansUX2
   * C2b) — `handleEditKeyResult`'s five properties, verbatim:
   *
   * 1. **Store-fresh, whole-array replacement.** Same `objective_update`
   *    truncation risk as every other results write (ADR §3, §1e).
   * 2. **Non-key results are carried through untouched**, by reference.
   * 3. **Only the toggled row passes through `createResult1`**, bounding its
   *    `owner`-dropping blast radius to the one row the user clicked.
   * 4. **The catch on a legacy row (A3c-i) becomes "no dispatch"**, exactly
   *    like a text edit: a row with neither `deadline` nor `forIntervalEnding`
   *    cannot be toggled.
   * 5. **`isKeyResult` is preserved, never set** (A3d) — the guard bounces a
   *    concurrent remote demotion instead of silently reverting it.
   *
   * One property this handler adds beyond `handleEditKeyResult`: an **INV-9
   * no-op guard on `delivered` itself** (`target.delivered === delivered`),
   * because unlike a text edit, a checkbox's "current value" is always well
   * defined and a redundant toggle is a real, reachable gesture — a
   * double-click, or two collaborators activating the same box.
   *
   * **Returns `boolean`, unlike every write handler above** — the one
   * property this task adds to the established shape (ADR C6b). Every guard
   * below is a place where a wrong implementation would write instead of
   * declining; the canvas reads the return value to decide whether checking
   * the last key result on a card is safe to chase with a pending-focus
   * record (a checked-but-undelivered result never got there, so no record
   * should be armed for it).
   */
  const handleToggleKeyResultDelivered = useCallback(
    (objectiveUuid: string, resultUuid: string, delivered: boolean): boolean => {
      if (!canEdit) return false;
      const fresh = store
        .getState()
        .map3Data.objectives.find((o) => o.uuid === objectiveUuid);
      const target = fresh?.results?.find((r) => r.uuid === resultUuid);
      if (!fresh || !target || !target.isKeyResult) return false; // INV-14
      if (target.delivered === delivered) return false; // INV-9 no-op
      let edited: Result1;
      try {
        edited = createResult1({ ...target, delivered });
      } catch {
        return false; // A3c-i
      }
      dispatch(
        updateObjective({
          uuid: objectiveUuid,
          updates: {
            results: (fresh.results ?? []).map((r) => (r.uuid === resultUuid ? edited : r)),
          },
        }),
      );
      return true;
    },
    [dispatch, store, canEdit],
  );

  /**
   * Persists an `additionalInfo` edit from the details dialog (ADR A4c).
   * `{ additionalInfo }` **only** — never bundled with `text` or `results`,
   * matching the one-field-per-intent shape every other handler here uses.
   * Two rules differ from the title contract, deliberately:
   *
   * - **Empty is a legal value.** Clearing an optional descriptive field is a
   *   real user intent, unlike a title. The handler writes `''`, never
   *   `undefined` (P1: `objective_update` has no `stripUndefinedFields`).
   * - **Trim on both sides.** Comparing a trimmed draft against a raw stored
   *   value while writing the raw draft stores `'foo '` into an empty field
   *   and then re-dispatches forever on every subsequent save of the same
   *   visible content — the write is the trimmed value, matching the shipped
   *   title contract (`handleEditTitle` above).
   *
   * A store-fresh read, not the render-time `card` prop, so a no-op compare
   * cannot go stale relative to a concurrent collaborator's write.
   */
  const handleEditAdditionalInfo = useCallback(
    (objectiveUuid: string, text: string) => {
      const trimmed = text.trim();
      if (!canEdit) return;
      const fresh = store
        .getState()
        .map3Data.objectives.find((o) => o.uuid === objectiveUuid);
      if (!fresh) return;
      if (trimmed === (fresh.additionalInfo ?? '')) return; // INV-9 no-op
      dispatch(updateObjective({ uuid: objectiveUuid, updates: { additionalInfo: trimmed } }));
    },
    [dispatch, store, canEdit],
  );

  /**
   * The map's intervals at its own grain (ADR.ImplementationPlansUX2 C1c) —
   * the search space `deriveCards` locates each objective's latest status in.
   * Memoized on `[milestoneMap]` **alone**: objectives churn far more often
   * than the map document, and rebuilding hundreds of `Date`s per objective
   * edit is the cost this memo exists to avoid.
   *
   * `cardStatusIntervals` catches `getIntervalsForMilestoneMap`'s
   * `default: throw` — at a click site an unsupported `interval` is one failed
   * action, but here it is render-time and would blank the whole wrapper.
   */
  const intervals = useMemo(
    () => (milestoneMap ? cardStatusIntervals(milestoneMap) : []),
    [milestoneMap],
  );

  const cards = useMemo(() => {
    if (!milestoneMap) return [];
    return deriveCards(cloneDeep(milestoneMap), cloneDeep(objectives), intervals);
  }, [milestoneMap, objectives, intervals]);

  const layoutKey = useMemo(() => boardLayoutKey(cards), [cards]);

  if (!milestoneMap) {
    return (
      <section aria-label="Implementation plans">
        <div role="status" className="p-8 text-center text-zinc-500">
          Loading implementation plans…
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Implementation plans">
      {/* key={mapUuid} (ADR.ImplementationPlansUX2 C4b): the canvas's TWO
          session-only view states — collapse (ADR B1) and hide-done (C4a) —
          live in plain useState with no map dimension of their own. Nothing
          in this subsystem ever unmounts on a navigation a user performs
          (Map3Workspace hides an inactive named view rather than unmounting
          it, and the route has no key), so neither state would otherwise
          reset on a map switch — a real, unnoticed leak for collapse that
          predates this round (fact 11/12). Remounting the canvas on every
          map uuid closes both at the mount site, without adding a slice
          field, an action, a selector or a map-keyed record (Alternative
          I2). */}
      <Map3ImplementationPlans
        key={mapUuid}
        cards={cards}
        layoutKey={layoutKey}
        canEdit={canEdit}
        onMoveCard={handleMoveCard}
        onEditTitle={handleEditTitle}
        onAddKeyResult={handleAddKeyResult}
        onEditKeyResult={handleEditKeyResult}
        onToggleKeyResultDelivered={handleToggleKeyResultDelivered}
        onEditAdditionalInfo={handleEditAdditionalInfo}
      />
    </section>
  );
}
